import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  VpnKey as KeyIcon, Home as HomeIcon, ContentCopy as CloneIcon,
} from '@mui/icons-material';
import { useRoleContext } from '../contexts/RoleContext';
import TenantHomeDestinationPanel from '../components/TenantHomeDestinationPanel';
import {
  cloneJurisdictionPackageApi,
  getJurisdictionPackageApi,
  listJurisdictionsApi,
  patchJurisdictionLiteApi,
} from '../api/applicationType.api';
import { getHybridDemoRegistryApi } from '../api/demoAssistants.api';

const emptyHybridDemos = () => ({
  enabled: false,
  defaultDemoId: 'recruiting',
  demos: [],
});

function TenantManagement() {
  const { userRole, hasPermission } = useRoleContext();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const emptyJurisdiction = {
    name: '',
    packageId: '',
    timezone: 'America/Los_Angeles',
    permitFeeScheduleId: '',
  };
  const [form, setForm] = useState({
    name: '', domain: '', address: '', phone: '', website: '',
    jurisdiction: { ...emptyJurisdiction },
    publicExperiences: { hybridDemos: emptyHybridDemos() },
  });
  const [hybridRegistry, setHybridRegistry] = useState([]);
  const [permitSchedules, setPermitSchedules] = useState([]);
  const [jurisdictionPackages, setJurisdictionPackages] = useState([]);
  const [cloneFrom, setCloneFrom] = useState('demo-generic');
  const [packageBusy, setPackageBusy] = useState(false);
  const [packageMessage, setPackageMessage] = useState(null);
  const emptyLite = {
    name: '',
    disclaimer: '',
    addressLabel: '',
    addressPlaceholder: '',
    feeNotes: '',
    feeBuildingBase: '',
    feeZoningBase: '',
    feeBusinessBase: '',
    officialApplyUrl: '',
    defaultFormTitle: '',
    mapLat: '',
    mapLng: '',
    portalBusinessTitle: '',
    portalBusinessSubtitle: '',
    portalBusinessColor: '',
    portalResidentialTitle: '',
    portalResidentialSubtitle: '',
    portalResidentialColor: '',
    portalEventsTitle: '',
    portalEventsSubtitle: '',
    portalEventsColor: '',
    tenantDomain: '',
  };
  const [liteForm, setLiteForm] = useState({ ...emptyLite });
  const [llmKeysDialogOpen, setLlmKeysDialogOpen] = useState(false);
  const [llmKeysForm, setLlmKeysForm] = useState({
    openai: '',
    openai_url: '',
    llama4: '',
    llama4_url: '',
    llama3: '',
    llama3_url: '',
    deepseek: '',
    deepseek_url: '',
    gpt_oss: '',
    gpt_oss_url: '',
    mixtral: '',
    mixtral_url: '',
    codellama: '',
    codellama_url: '',
    openaa: '',
    openaa_url: '',
    gemini: '',
    gemini_url: '',
    anthropic: '',
    anthropic_url: '',
    azure: '',
    azure_url: '',
    mistral: '',
    mistral_url: '',
    cohere: '',
    cohere_url: '',
    together: '',
    together_url: '',
    custom: '',
    custom_url: '',
  });
  const [llmKeysTenant, setLlmKeysTenant] = useState(null);
  const [homeDialogOpen, setHomeDialogOpen] = useState(false);
  const [homeTenant, setHomeTenant] = useState(null);

  // LLM model options for dropdown
  const llmModelOptions = [
    { key: 'openai', label: 'OpenAI' },
    { key: 'llama4', label: 'Llama 4' },
    { key: 'llama3', label: 'Llama 3' },
    { key: 'deepseek', label: 'DeepSeek' },
    { key: 'gpt-oss', label: 'GPT-OSS' },
    { key: 'mixtral', label: 'Mixtral' },
    { key: 'codellama', label: 'Code LLaMA' },
    { key: 'openaa', label: 'OpenAA' },
    { key: 'gemini', label: 'Gemini' },
    { key: 'anthropic', label: 'Anthropic' },
    { key: 'azure', label: 'Azure' },
    { key: 'mistral', label: 'Mistral' },
    { key: 'cohere', label: 'Cohere' },
    { key: 'together', label: 'Together' },
    { key: 'custom', label: 'Custom' },
  ];

  const [selectedLlmModel, setSelectedLlmModel] = useState('openai');

  // Only allow super_admin
  if (userRole !== 'super_admin') {
    // Prevent infinite reload loop by NOT redirecting or reloading here
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography>You do not have permission to view this page.</Typography>
      </Box>
    );
  }

  const loadTenants = async () => {
    setLoading(true);
    try {
      // --- PATCH: Always send Authorization header if JWT exists ---
      const tkn = localStorage.getItem('tkn');
      const headers = tkn
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` }
        : { 'Content-Type': 'application/json' };
      const res = await fetch('/api/v1/tenants', {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (res.status === 401) {
        // Do NOT reload or redirect here, just show access denied
        setLoading(false);
        setTenants([]);
        return;
      }
      const data = await res.json();
      setTenants(data.tenants || []);
    } catch (err) {
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const hybridFromTenant = (tenant) => {
    const h = tenant?.publicExperiences?.hybridDemos;
    if (!h) return emptyHybridDemos();
    return {
      enabled: h.enabled !== false && Array.isArray(h.demos) && h.demos.length > 0,
      defaultDemoId: h.defaultDemoId || h.demos?.[0]?.id || 'recruiting',
      demos: (h.demos || []).map((d) => (typeof d === 'string'
        ? { id: d, visibleOnLanding: true }
        : { id: d.id, visibleOnLanding: d.visibleOnLanding !== false })),
    };
  };

  const handleOpenDialog = (tenant = null) => {
    setEditTenant(tenant);
    setPackageMessage(null);
    setForm(
      tenant
        ? {
          name: tenant.name, domain: tenant.domain, address: tenant.address, phone: tenant.phone, website: tenant.website,
          jurisdiction: tenant.jurisdiction
            ? {
              name: tenant.jurisdiction.name || '',
              packageId: tenant.jurisdiction.packageId || '',
              timezone: tenant.jurisdiction.timezone || 'America/Los_Angeles',
              permitFeeScheduleId: tenant.jurisdiction.permitFeeScheduleId || '',
            }
            : { ...emptyJurisdiction },
          publicExperiences: { hybridDemos: hybridFromTenant(tenant) },
        }
        : {
          name: '', domain: '', address: '', phone: '', website: '',
          jurisdiction: { ...emptyJurisdiction },
          publicExperiences: { hybridDemos: emptyHybridDemos() },
        },
    );
    setCloneFrom(tenant?.jurisdiction?.packageId || 'demo-generic');
    setLiteForm({ ...emptyLite });
    setDialogOpen(true);
    getHybridDemoRegistryApi()
      .then((res) => setHybridRegistry(Array.isArray(res?.demos) ? res.demos : []))
      .catch(() => setHybridRegistry([]));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditTenant(null);
    setPackageMessage(null);
    setLiteForm({ ...emptyLite });
    setForm({
      name: '', domain: '', address: '', phone: '', website: '',
      jurisdiction: { ...emptyJurisdiction },
      publicExperiences: { hybridDemos: emptyHybridDemos() },
    });
  };

  const toggleHybridDemo = (demoId, checked) => {
    setForm((prev) => {
      const hybrid = prev.publicExperiences?.hybridDemos || emptyHybridDemos();
      let demos = [...(hybrid.demos || [])];
      if (checked) {
        if (!demos.some((d) => d.id === demoId)) {
          demos.push({ id: demoId, visibleOnLanding: true });
        }
      } else {
        demos = demos.filter((d) => d.id !== demoId);
      }
      return {
        ...prev,
        publicExperiences: {
          hybridDemos: {
            ...hybrid,
            enabled: demos.length > 0 && hybrid.enabled !== false,
            demos,
            defaultDemoId: demos.some((d) => d.id === hybrid.defaultDemoId)
              ? hybrid.defaultDemoId
              : demos[0]?.id || 'recruiting',
          },
        },
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name?.startsWith('jurisdiction.')) {
      const key = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        jurisdiction: { ...prev.jurisdiction, [key]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const loadPermitSchedules = async () => {
    try {
      const tkn = localStorage.getItem('tkn');
      const headers = tkn ? { Authorization: `Bearer ${tkn}` } : {};
      const res = await fetch('/api/v1/permit-fees', { credentials: 'include', headers });
      if (res.ok) {
        const data = await res.json();
        setPermitSchedules(data.schedules || []);
      }
    } catch (_) {}
  };

  const loadJurisdictionPackages = async () => {
    try {
      const list = await listJurisdictionsApi();
      setJurisdictionPackages(Array.isArray(list) ? list : []);
    } catch (_) {
      setJurisdictionPackages([]);
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      loadPermitSchedules();
      loadJurisdictionPackages();
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen || !form.jurisdiction?.packageId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getJurisdictionPackageApi(form.jurisdiction.packageId);
        if (cancelled) return;
        const intake = data?.intake || {};
        const addressField = (intake.formFields || []).find((f) => f.id === 'address' || f.name === 'address');
        const business = intake.portals?.business || {};
        const residential = intake.portals?.residential || {};
        const events = intake.portals?.events || {};
        const schedule = data?.discoveryExtras?.feeSchedule || {};
        setLiteForm({
          name: data?.name || form.jurisdiction?.name || '',
          disclaimer: data?.disclaimer || data?.catalog?.disclaimer || '',
          addressLabel: addressField?.label || '',
          addressPlaceholder: addressField?.placeholder || '',
          feeNotes: data?.discoveryExtras?.feeNotes
            || schedule.notes
            || '',
          feeBuildingBase: schedule.building_permit?.base ?? '',
          feeZoningBase: schedule.zoning_compliance?.base ?? '',
          feeBusinessBase: schedule.business_registration?.base ?? '',
          officialApplyUrl: data?.catalog?.officialApplyUrl || data?.officialApplyUrl || '',
          defaultFormTitle: intake.defaultFormTitle || '',
          mapLat: intake.map?.defaultCenter?.lat ?? '',
          mapLng: intake.map?.defaultCenter?.lng ?? '',
          portalBusinessTitle: business.title || '',
          portalBusinessSubtitle: business.subtitle || '',
          portalBusinessColor: business.theme?.primaryColor || '',
          portalResidentialTitle: residential.title || '',
          portalResidentialSubtitle: residential.subtitle || '',
          portalResidentialColor: residential.theme?.primaryColor || '',
          portalEventsTitle: events.title || '',
          portalEventsSubtitle: events.subtitle || '',
          portalEventsColor: events.theme?.primaryColor || '',
          tenantDomain: editTenant?.domain || '',
        });
      } catch (_) {
        /* package may not exist yet */
      }
    })();
    return () => { cancelled = true; };
  }, [dialogOpen, form.jurisdiction?.packageId]);

  const handleSaveJurisdictionLite = async () => {
    const packageId = String(form.jurisdiction?.packageId || '').trim();
    if (!editTenant?._id || !packageId) {
      setPackageMessage({ severity: 'warning', text: 'Package id and saved tenant required.' });
      return;
    }
    setPackageBusy(true);
    setPackageMessage(null);
    try {
      const lat = Number(liteForm.mapLat);
      const lng = Number(liteForm.mapLng);
      const body = {
        tenantId: editTenant._id,
        name: liteForm.name || undefined,
        disclaimer: liteForm.disclaimer || undefined,
        addressLabel: liteForm.addressLabel || undefined,
        addressPlaceholder: liteForm.addressPlaceholder || undefined,
        feeNotes: liteForm.feeNotes || undefined,
        officialApplyUrl: liteForm.officialApplyUrl || undefined,
        defaultFormTitle: liteForm.defaultFormTitle || undefined,
        timezone: form.jurisdiction?.timezone || undefined,
      };
      const feeSchedule = {};
      const buildingBase = Number(liteForm.feeBuildingBase);
      const zoningBase = Number(liteForm.feeZoningBase);
      const businessBase = Number(liteForm.feeBusinessBase);
      if (Number.isFinite(buildingBase)) feeSchedule.building_permit = { base: buildingBase };
      if (Number.isFinite(zoningBase)) feeSchedule.zoning_compliance = { base: zoningBase };
      if (Number.isFinite(businessBase)) feeSchedule.business_registration = { base: businessBase };
      if (Object.keys(feeSchedule).length) body.feeSchedule = feeSchedule;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        body.mapCenter = { lat, lng };
      }
      const portalsPatch = {};
      const patchPortal = (id, title, subtitle, color) => {
        const p = {};
        if (title) p.title = title;
        if (subtitle) p.subtitle = subtitle;
        if (color) p.theme = { primaryColor: color };
        if (Object.keys(p).length) portalsPatch[id] = p;
      };
      patchPortal('business', liteForm.portalBusinessTitle, liteForm.portalBusinessSubtitle, liteForm.portalBusinessColor);
      patchPortal('residential', liteForm.portalResidentialTitle, liteForm.portalResidentialSubtitle, liteForm.portalResidentialColor);
      patchPortal('events', liteForm.portalEventsTitle, liteForm.portalEventsSubtitle, liteForm.portalEventsColor);
      if (Object.keys(portalsPatch).length) body.portals = portalsPatch;
      const result = await patchJurisdictionLiteApi(packageId, body);
      setForm((prev) => ({
        ...prev,
        jurisdiction: {
          ...prev.jurisdiction,
          name: result?.package?.name || liteForm.name || prev.jurisdiction?.name,
        },
      }));
      setPackageMessage({
        severity: 'success',
        text: `Updated lite package fields for "${packageId}".`,
      });
      loadTenants();
    } catch (err) {
      setPackageMessage({
        severity: 'error',
        text: err?.message || err?.error || 'Failed to save jurisdiction lite fields',
      });
    } finally {
      setPackageBusy(false);
    }
  };

  const handleAttachJurisdictionPackage = async () => {
    const packageId = String(form.jurisdiction?.packageId || '').trim();
    if (!editTenant?._id) {
      setPackageMessage({ severity: 'warning', text: 'Save the tenant first, then attach a package.' });
      return;
    }
    if (!packageId) {
      setPackageMessage({ severity: 'warning', text: 'Set a package id (e.g. berkeley) before attaching.' });
      return;
    }
    setPackageBusy(true);
    setPackageMessage(null);
    try {
      const result = await cloneJurisdictionPackageApi({
        id: packageId,
        from: cloneFrom || 'demo-generic',
        name: form.jurisdiction?.name || form.name || packageId,
        timezone: form.jurisdiction?.timezone,
        tenantId: editTenant._id,
        scope: 'tenant',
      });
      setForm((prev) => ({
        ...prev,
        jurisdiction: {
          ...prev.jurisdiction,
          packageId: result.packageId || packageId,
          name: result.name || prev.jurisdiction?.name,
        },
      }));
      setPackageMessage({
        severity: 'success',
        text: `Attached package "${result.packageId}" to this tenant (${result.source || 'mongo-tenant'}). Apps should use jurisdictionId "${result.packageId}".`,
      });
      loadTenants();
      loadJurisdictionPackages();
    } catch (err) {
      setPackageMessage({
        severity: 'error',
        text: err?.message || err?.error || 'Failed to attach jurisdiction package',
      });
    } finally {
      setPackageBusy(false);
    }
  };

  const handleSave = async () => {
    try {
      const tkn = localStorage.getItem('tkn');
      const headers = tkn
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` }
        : { 'Content-Type': 'application/json' };
      const method = editTenant ? 'PUT' : 'POST';
      const url = editTenant ? `/api/v1/tenants/${editTenant._id}` : '/api/v1/tenants';
      const payload = { ...form };
      if (form.jurisdiction?.permitFeeScheduleId === '') {
        payload.jurisdiction = { ...form.jurisdiction, permitFeeScheduleId: null };
      }
      const hybrid = form.publicExperiences?.hybridDemos || emptyHybridDemos();
      payload.publicExperiences = {
        hybridDemos: {
          enabled: Boolean(hybrid.enabled) && (hybrid.demos || []).length > 0,
          defaultDemoId: hybrid.defaultDemoId || null,
          demos: hybrid.demos || [],
        },
      };
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers,
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        window.location.href = '/signin';
        return;
      }
      handleCloseDialog();
      loadTenants();
    } catch (err) {
      // handle error
    }
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Delete tenant "${tenant.name}"?`)) return;
    try {
      const tkn = localStorage.getItem('tkn');
      const headers = tkn
        ? { Authorization: `Bearer ${tkn}` }
        : {};
      const res = await fetch(`/api/v1/tenants/${tenant._id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });
      if (res.status === 401) {
        window.location.href = '/signin';
        return;
      }
      loadTenants();
    } catch (err) {
      // handle error
    }
  };

  // Load LLM keys for a tenant (GET /api/v1/tenants/:id/llm-keys)
  const handleOpenLlmKeysDialog = async (tenant) => {
    setLlmKeysTenant(tenant);
    setSelectedLlmModel('openai');
    setLlmKeysForm({
      openai: '',
      openai_url: '',
      llama4: '',
      llama4_url: '',
      llama3: '',
      llama3_url: '',
      deepseek: '',
      deepseek_url: '',
      gpt_oss: '',
      gpt_oss_url: '',
      openaa: '',
      openaa_url: '',
      gemini: '',
      gemini_url: '',
      anthropic: '',
      anthropic_url: '',
      azure: '',
      azure_url: '',
      mistral: '',
      mistral_url: '',
      cohere: '',
      cohere_url: '',
      together: '',
      together_url: '',
      custom: '',
      custom_url: '',
    });
    try {
      const tkn = localStorage.getItem('tkn');
      const headers = tkn
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` }
        : { 'Content-Type': 'application/json' };
      const res = await fetch(`/api/v1/tenants/${tenant._id}/llm-keys`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        setLlmKeysForm({
          openai: data.openai || '',
          openai_url: data.openai_url || '',
          llama4: data.llama4 || '',
          llama4_url: data.llama4_url || '',
          llama3: data.llama3 || '',
          llama3_url: data.llama3_url || '',
          deepseek: data.deepseek || '',
          deepseek_url: data.deepseek_url || '',
          ollama: data.ollama || '',
          ollama_url: data.ollama_url || '',
          openaa: data.openaa || '',
          openaa_url: data.openaa_url || '',
          gemini: data.gemini || '',
          gemini_url: data.gemini_url || '',
          anthropic: data.anthropic || '',
          anthropic_url: data.anthropic_url || '',
          azure: data.azure || '',
          azure_url: data.azure_url || '',
          mistral: data.mistral || '',
          mistral_url: data.mistral_url || '',
          cohere: data.cohere || '',
          cohere_url: data.cohere_url || '',
          together: data.together || '',
          together_url: data.together_url || '',
          custom: data.custom || '',
          custom_url: data.custom_url || '',
        });
      }
    } catch (err) {
      // ignore error, show empty form
    }
    setLlmKeysDialogOpen(true);
  };

  const handleCloseLlmKeysDialog = () => {
    setLlmKeysDialogOpen(false);
    setLlmKeysTenant(null);
    setLlmKeysForm({ openai: '', llama4: '', ollama: '' });
  };

  // Ensure all values in llmKeysForm are always strings (never undefined or null)
  const handleLlmKeysChange = (e) => {
    const { name, value } = e.target;
    setLlmKeysForm((prev) => ({
      ...prev,
      [name]: value === undefined || value === null ? '' : value,
    }));
  };

  // Save LLM keys for a tenant (POST /api/v1/tenants/:id/llm-keys)
  const handleSaveLlmKeys = async () => {
    if (!llmKeysTenant) return;
    try {
      const tkn = localStorage.getItem('tkn');
      const headers = tkn
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` }
        : { 'Content-Type': 'application/json' };
      // Save all keys at once, not just the selected model
      const payload = { ...llmKeysForm };
      // No special handling for llama3 or deepseek-v2, just save their key/url
      await fetch(`/api/v1/tenants/${llmKeysTenant._id}/llm-keys`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload),
      });
      handleCloseLlmKeysDialog();
    } catch (err) {
      // handle error
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h4">Tenant / Company Management</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Tenant
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTenants}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Package</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((tenant) => (
              <TableRow key={tenant._id}>
                <TableCell>{tenant.name}</TableCell>
                <TableCell>{tenant.domain}</TableCell>
                <TableCell>{tenant.jurisdiction?.packageId || '—'}</TableCell>
                <TableCell>{tenant.address}</TableCell>
                <TableCell>{tenant.phone}</TableCell>
                <TableCell>{tenant.website}</TableCell>
                <TableCell>{tenant.isActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleOpenDialog(tenant)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="LLM API Keys">
                    <IconButton color="primary" onClick={() => handleOpenLlmKeysDialog(tenant)}>
                      <KeyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Post-login home">
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setHomeTenant(tenant);
                        setHomeDialogOpen(true);
                      }}
                    >
                      <HomeIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton color="error" onClick={() => handleDelete(tenant)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {tenants.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No tenants found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editTenant ? 'Edit Tenant' : 'Add Tenant'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Domain"
            name="domain"
            value={form.domain}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Address"
            name="address"
            value={form.address}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Website"
            name="website"
            value={form.website}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Public hybrid demos</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Opt-in for this tenant&apos;s Host. Platform apex (interviewflow.studio) always shows the full registry.
            Docs: Studio Help → Tenant &amp; app metadata.
          </Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(form.publicExperiences?.hybridDemos?.enabled)
                  && (form.publicExperiences?.hybridDemos?.demos || []).length > 0}
                onChange={(e) => {
                  const on = e.target.checked;
                  setForm((prev) => {
                    const allIds = hybridRegistry.map((d) => d.id);
                    const demos = on
                      ? (prev.publicExperiences?.hybridDemos?.demos?.length
                        ? prev.publicExperiences.hybridDemos.demos
                        : allIds.map((id) => ({ id, visibleOnLanding: true })))
                      : [];
                    return {
                      ...prev,
                      publicExperiences: {
                        hybridDemos: {
                          enabled: on && demos.length > 0,
                          defaultDemoId: demos[0]?.id || 'recruiting',
                          demos,
                        },
                      },
                    };
                  });
                }}
              />
            )}
            label="Enable hybrid demos on this tenant host"
          />
          <Stack spacing={0.25} sx={{ mb: 1, pl: 1 }}>
            {(hybridRegistry.length ? hybridRegistry : [
              { id: 'recruiting', title: 'Hiring' },
              { id: 'returns', title: 'Returns' },
              { id: 'personal', title: 'Personal' },
              { id: 'realestate', title: 'Home search' },
              { id: 'listings', title: 'Listings' },
            ]).map((d) => {
              const selected = (form.publicExperiences?.hybridDemos?.demos || [])
                .some((x) => x.id === d.id);
              return (
                <FormControlLabel
                  key={d.id}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={(e) => toggleHybridDemo(d.id, e.target.checked)}
                    />
                  )}
                  label={`${d.title || d.id} (${d.path || `/v2/demo/${d.id}`})`}
                />
              );
            })}
          </Stack>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Default demo</InputLabel>
            <Select
              label="Default demo"
              value={form.publicExperiences?.hybridDemos?.defaultDemoId || ''}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                publicExperiences: {
                  hybridDemos: {
                    ...(prev.publicExperiences?.hybridDemos || emptyHybridDemos()),
                    defaultDemoId: e.target.value,
                  },
                },
              }))}
            >
              {(form.publicExperiences?.hybridDemos?.demos || []).map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.id}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Jurisdiction</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            City packages are tenant-scoped. Set package id, then Attach/refresh to clone a template onto this tenant.
            Studio Apps should use the same id as metadata.interviewFlow.jurisdictionId.
          </Typography>
          <TextField
            label="Jurisdiction name"
            name="jurisdiction.name"
            value={form.jurisdiction?.name || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            placeholder="e.g. City of San Francisco"
          />
          <TextField
            label="Jurisdiction package id"
            name="jurisdiction.packageId"
            value={form.jurisdiction?.packageId || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            placeholder="e.g. san-francisco, berkeley"
            helperText="Slug used by Apps (jurisdictionId). New cities: CLI onboard-city, or attach here onto an existing tenant."
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Known packages</InputLabel>
            <Select
              label="Known packages"
              value={form.jurisdiction?.packageId && jurisdictionPackages.some((p) => p.id === form.jurisdiction.packageId)
                ? form.jurisdiction.packageId
                : ''}
              onChange={(e) => {
                const id = e.target.value;
                const pkg = jurisdictionPackages.find((p) => p.id === id);
                setForm((prev) => ({
                  ...prev,
                  jurisdiction: {
                    ...prev.jurisdiction,
                    packageId: id,
                    name: prev.jurisdiction?.name || pkg?.name || id,
                    timezone: prev.jurisdiction?.timezone || pkg?.timezone || 'America/Los_Angeles',
                  },
                }));
                setCloneFrom(id || 'demo-generic');
              }}
            >
              <MenuItem value="">Custom (type package id above)</MenuItem>
              {jurisdictionPackages.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.id}
                  {' — '}
                  {p.name}
                  {p.parcelAdapter && p.parcelAdapter !== 'none' ? ` (${p.parcelAdapter})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Timezone"
            name="jurisdiction.timezone"
            value={form.jurisdiction?.timezone || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            placeholder="America/Los_Angeles"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Permit fee schedule</InputLabel>
            <Select
              name="jurisdiction.permitFeeScheduleId"
              value={form.jurisdiction?.permitFeeScheduleId || ''}
              label="Permit fee schedule"
              onChange={(e) => setForm((prev) => ({
                ...prev,
                jurisdiction: { ...prev.jurisdiction, permitFeeScheduleId: e.target.value },
              }))}
            >
              <MenuItem value="">None</MenuItem>
              {permitSchedules.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.permitType}
                  {' '}
                  (
                  {s.category}
                  ) — $
                  {s.baseFee}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {editTenant && form.jurisdiction?.packageId && (
            <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                City package (lite)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Edit display name, address labels, fee notes, map center, and business portal chrome without raw JSON.
              </Typography>
              <TextField
                label="Package display name"
                size="small"
                fullWidth
                margin="dense"
                value={liteForm.name}
                onChange={(e) => setLiteForm((p) => ({ ...p, name: e.target.value }))}
              />
              <TextField
                label="Disclaimer"
                size="small"
                fullWidth
                margin="dense"
                multiline
                minRows={2}
                value={liteForm.disclaimer}
                onChange={(e) => setLiteForm((p) => ({ ...p, disclaimer: e.target.value }))}
              />
              <TextField
                label="Address field label"
                size="small"
                fullWidth
                margin="dense"
                value={liteForm.addressLabel}
                onChange={(e) => setLiteForm((p) => ({ ...p, addressLabel: e.target.value }))}
                placeholder="Street address in …"
              />
              <TextField
                label="Address placeholder"
                size="small"
                fullWidth
                margin="dense"
                value={liteForm.addressPlaceholder}
                onChange={(e) => setLiteForm((p) => ({ ...p, addressPlaceholder: e.target.value }))}
              />
              <TextField
                label="Default form title"
                size="small"
                fullWidth
                margin="dense"
                value={liteForm.defaultFormTitle}
                onChange={(e) => setLiteForm((p) => ({ ...p, defaultFormTitle: e.target.value }))}
              />
              <TextField
                label="Fee notes"
                size="small"
                fullWidth
                margin="dense"
                multiline
                minRows={2}
                value={liteForm.feeNotes}
                onChange={(e) => setLiteForm((p) => ({ ...p, feeNotes: e.target.value }))}
                helperText="Shown in discovery fee estimates / staff notes"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                Illustrative fee bases (USD) — citizen outcome tray / fee estimates
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Building permit $"
                  size="small"
                  margin="dense"
                  type="number"
                  value={liteForm.feeBuildingBase}
                  onChange={(e) => setLiteForm((p) => ({ ...p, feeBuildingBase: e.target.value }))}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Zoning review $"
                  size="small"
                  margin="dense"
                  type="number"
                  value={liteForm.feeZoningBase}
                  onChange={(e) => setLiteForm((p) => ({ ...p, feeZoningBase: e.target.value }))}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Business reg $"
                  size="small"
                  margin="dense"
                  type="number"
                  value={liteForm.feeBusinessBase}
                  onChange={(e) => setLiteForm((p) => ({ ...p, feeBusinessBase: e.target.value }))}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                label="Official apply URL"
                size="small"
                fullWidth
                margin="dense"
                value={liteForm.officialApplyUrl}
                onChange={(e) => setLiteForm((p) => ({ ...p, officialApplyUrl: e.target.value }))}
                placeholder="https://… (link-out, not filing)"
                helperText="Shown on citizen outcomes as “Apply at city” when set"
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Map lat"
                  size="small"
                  margin="dense"
                  type="number"
                  value={liteForm.mapLat}
                  onChange={(e) => setLiteForm((p) => ({ ...p, mapLat: e.target.value }))}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Map lng"
                  size="small"
                  margin="dense"
                  type="number"
                  value={liteForm.mapLng}
                  onChange={(e) => setLiteForm((p) => ({ ...p, mapLng: e.target.value }))}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
                Portal chrome (CMS-lite) — titles, subtitles, primary colors
              </Typography>
              {editTenant?.domain && (
                <Typography variant="caption" display="block" sx={{ mb: 1 }} color="text.secondary">
                  Vanity host:
                  {' '}
                  <strong>{editTenant.domain}</strong>
                  {' '}
                  → /advisor
                </Typography>
              )}
              {[
                ['Business', 'portalBusinessTitle', 'portalBusinessSubtitle', 'portalBusinessColor'],
                ['Residential', 'portalResidentialTitle', 'portalResidentialSubtitle', 'portalResidentialColor'],
                ['Events', 'portalEventsTitle', 'portalEventsSubtitle', 'portalEventsColor'],
              ].map(([label, titleKey, subKey, colorKey]) => (
                <Box key={label} sx={{ mb: 1 }}>
                  <TextField
                    label={`${label} title`}
                    size="small"
                    fullWidth
                    margin="dense"
                    value={liteForm[titleKey]}
                    onChange={(e) => setLiteForm((p) => ({ ...p, [titleKey]: e.target.value }))}
                  />
                  <TextField
                    label={`${label} subtitle`}
                    size="small"
                    fullWidth
                    margin="dense"
                    value={liteForm[subKey]}
                    onChange={(e) => setLiteForm((p) => ({ ...p, [subKey]: e.target.value }))}
                  />
                  <TextField
                    label={`${label} color`}
                    size="small"
                    fullWidth
                    margin="dense"
                    value={liteForm[colorKey]}
                    onChange={(e) => setLiteForm((p) => ({ ...p, [colorKey]: e.target.value }))}
                    placeholder="#1565c0"
                  />
                </Box>
              ))}
              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                disabled={packageBusy}
                onClick={handleSaveJurisdictionLite}
              >
                Save package lite fields
              </Button>
            </Box>
          )}
          {editTenant && (
            <Box sx={{ mt: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Attach / refresh city package
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Clone from template</InputLabel>
                <Select
                  label="Clone from template"
                  value={cloneFrom}
                  onChange={(e) => setCloneFrom(e.target.value)}
                >
                  {(jurisdictionPackages.length
                    ? jurisdictionPackages
                    : [{ id: 'demo-generic', name: 'Demo generic' }, { id: 'san-francisco', name: 'San Francisco' }]
                  ).map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.id}
                      {' — '}
                      {p.name || p.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={packageBusy ? <CircularProgress size={16} /> : <CloneIcon />}
                disabled={packageBusy || !form.jurisdiction?.packageId}
                onClick={handleAttachJurisdictionPackage}
              >
                Attach / refresh package on this tenant
              </Button>
              {packageMessage && (
                <Alert severity={packageMessage.severity} sx={{ mt: 1 }}>
                  {packageMessage.text}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editTenant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* LLM Keys Dialog */}
      <Dialog open={llmKeysDialogOpen} onClose={handleCloseLlmKeysDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          LLM API Keys for
          {' '}
          {llmKeysTenant?.name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Model Provider</InputLabel>
            <Select
              value={selectedLlmModel}
              label="Model Provider"
              onChange={(e) => setSelectedLlmModel(e.target.value)}
            >
              {llmModelOptions.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={`${llmModelOptions.find((opt) => opt.key === selectedLlmModel)?.label || 'Model'} API Key`}
            name={selectedLlmModel}
            value={llmKeysForm[selectedLlmModel] ?? ''}
            onChange={handleLlmKeysChange}
            fullWidth
            margin="normal"
            type="password"
            autoComplete="off"
          />
          <TextField
            label={`${llmModelOptions.find((opt) => opt.key === selectedLlmModel)?.label || 'Model'} API URL`}
            name={`${selectedLlmModel}_url`}
            value={llmKeysForm[`${selectedLlmModel}_url`] ?? ''}
            onChange={handleLlmKeysChange}
            fullWidth
            margin="normal"
            type="url"
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLlmKeysDialog}>Cancel</Button>
          <Button onClick={handleSaveLlmKeys} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={homeDialogOpen}
        onClose={() => {
          setHomeDialogOpen(false);
          setHomeTenant(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Post-login home —
          {' '}
          {homeTenant?.name}
        </DialogTitle>
        <DialogContent>
          {homeTenant && (
            <TenantHomeDestinationPanel
              tenantId={homeTenant._id}
              initialDestination={homeTenant.homeDestination}
              onSaved={(homeDestination) => {
                setTenants((prev) => prev.map((t) => (
                  t._id === homeTenant._id ? { ...t, homeDestination } : t
                )));
                setHomeTenant((prev) => (prev ? { ...prev, homeDestination } : prev));
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHomeDialogOpen(false); setHomeTenant(null); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TenantManagement;
